import { useEffect, useState } from 'react';
import { compensationGateway } from '../../compensation';
import type { DeductionElectionResponse } from '../../compensation/types';
import { payConfigGateway } from '../../pay-config';
import type { DeductionCodeResponse } from '../../pay-config/types';

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Shown as checked while it has no end date. The server only accepts an end date AFTER the start date, so unchecking an election that started
 * today ends it tomorrow; it reads as unchecked straight away (with an "Ends ..." note while it is still running) and the row stays in history. */
function isSelected(e: DeductionElectionResponse): boolean {
  return e.effectiveEndDate === null;
}

function dayAfter(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * "Select Deductions" (Figma admin screen 17). One checkbox per ACTIVE deduction code the company has already set up in Payroll Configuration; checking
 * one creates that employee's effective-dated deduction election from today, unchecking ends it today (history is kept, nothing is deleted). The code's
 * own default amount applies: this control never types an amount, a rate or a tax treatment, and it never creates a deduction code. Whether an
 * employee is eligible is decided by the server, whose refusal is shown and leaves the checkbox as it was. Overrides and dates stay in
 * Additional Settings > Benefits / Deductions.
 */
export function PayrollDeductionsSelector({ employeeId }: { employeeId: string }) {
  const [codes, setCodes] = useState<DeductionCodeResponse[] | null>(null);
  const [elections, setElections] = useState<DeductionElectionResponse[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([payConfigGateway.listDeductionCodes(), compensationGateway.listDeductionElections(employeeId)])
      .then(([c, e]) => {
        if (cancelled) return;
        setCodes(c.filter((x) => x.isActive));
        setElections(e);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the deductions.');
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const today = todayIso();
  const activeFor = (codeId: string) => elections.find((e) => e.deductionCodeId === codeId && isSelected(e));
  const endingFor = (codeId: string) => elections.find((e) => e.deductionCodeId === codeId && e.effectiveEndDate !== null && e.effectiveEndDate > today);

  async function toggle(code: DeductionCodeResponse, on: boolean) {
    setError(null);
    setBusyCode(code.id);
    try {
      if (on) {
        const created = await compensationGateway.createDeductionElection(employeeId, {
          deductionCodeId: code.id, amountOverride: null, companyContributionOverride: null, effectiveStartDate: today,
          // Ticking a deduction that was scheduled to end brings it back: it replaces that assignment from today.
          replaceCurrent: true,
        });
        setElections((prev) => [...prev, created]);
      } else {
        const active = activeFor(code.id);
        if (active) {
          const ended = await compensationGateway.endDeductionElection(active.id, active.effectiveStartDate < today ? today : dayAfter(active.effectiveStartDate));
          setElections((prev) => prev.map((e) => (e.id === ended.id ? ended : e)));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not update ${code.name}.`);
    } finally {
      setBusyCode(null);
    }
  }

  return (
    <fieldset className="payroll-deductions" style={{ border: 'none', padding: 0, marginTop: 16 }}>
      <legend><strong>Select Deductions:</strong></legend>
      {loadError && <p className="error" role="alert">{loadError}</p>}
      {codes && codes.length === 0 && <p className="muted">No deductions are set up for your company yet. Add them under Payroll settings first.</p>}
      {codes && codes.length > 0 && (
        <div className="payroll-deductions__grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {codes.map((code) => (
            <label key={code.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(activeFor(code.id))}
                disabled={busyCode !== null}
                onChange={(e) => void toggle(code, e.target.checked)}
              />
              {code.name}
              {endingFor(code.id) && <span className="muted"> (ends {endingFor(code.id)!.effectiveEndDate})</span>}
            </label>
          ))}
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <p className="muted">
        Each deduction uses the amount set up for it in Payroll settings. To change an amount or an end date, use Additional Settings, Benefits / Deductions.
        Checking one does not set a tax rule or change any past pay.
      </p>
    </fieldset>
  );
}
